import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import apiConfig from "../utils/apiConfig";
import {
  IAllSitesResponse,
  IAssignSiteLocationsRequest,
  ISiteRequest,
  ISiteSummariesResponse,
} from "../models/Site";

export default function useSites() {
  const { get, post, put, del } = useApiHelper(apiConfig("useSites"));
  const queryClient = useQueryClient();

  const sites = useQuery({
    queryKey: ["sites"],
    queryFn: () => get<IAllSitesResponse>("/site"),
    staleTime: 1000 * 60 * 10, //10 minutes
  });

  const summaries = useQuery({
    queryKey: ["site-summaries"],
    queryFn: () => get<ISiteSummariesResponse>("/site/summary"),
    staleTime: 1000 * 60 * 2, //2 minutes
  });

  // Assigning locations changes which site a location belongs to, so the yard and warehouse
  // location lists are stale too - their dropdowns and tables show the site.
  const invalidate = () => {
    queryClient.invalidateQueries(["sites"]);
    queryClient.invalidateQueries(["site-summaries"]);
    queryClient.invalidateQueries(["locations"]);
    queryClient.invalidateQueries(["warehouseLocations"]);
  };

  const create = useMutation(
    (site: ISiteRequest) => post("/site", site),
    { onSuccess: invalidate }
  );

  const update = useMutation(
    ({ siteId, site }: { siteId: string; site: ISiteRequest }) => put(`/site/${siteId}`, site),
    { onSuccess: invalidate }
  );

  const remove = useMutation(
    (siteId: string) => del(`/site/${siteId}`),
    { onSuccess: invalidate }
  );

  const assignLocations = useMutation(
    ({ siteId, request }: { siteId: string; request: IAssignSiteLocationsRequest }) =>
      put(`/site/${siteId}/locations`, request),
    { onSuccess: invalidate }
  );

  const unassignLocations = useMutation(
    (request: IAssignSiteLocationsRequest) => put("/site/unassign-locations", request),
    { onSuccess: invalidate }
  );

  return {
    sites,
    summaries,
    create,
    update,
    remove,
    assignLocations,
    unassignLocations,
  };
}
